import { ApprovedSeriesProperties } from "features/approvedSeries/approvedSeries-property-definitions";
import { SeriesProperties } from "features/series/series-property-definitions";
import { SeasonProperties } from 'features/season/season-property-definitions';
import { ProcessedMediaProperties } from 'features/processedMedia/processedMedia-property-definitions'
import { CuratedMediaListProperties } from 'features/curatedMediaList/curatedMediaList-property-definitions';
import { CuratedSeriesListProperties } from 'features/curatedSeriesList/curatedSeriesList-property-definitions';
import { ApprovedMedia, ApprovedSeries, CuratedMediaList, CuratedSeriesList, Media, ProcessedMedia, ProcessedMediaStates } from "models";
import { MediaType, ResourcePropertyType } from "features/enumerations";


type AnyObject = { [key: string]: any };

function getDeepProperty(obj: AnyObject, path: string): any {
    const parts = path.split(".");
    let current = obj;
    for (let part of parts) {
        if (!current || current[part] === undefined) {
            return undefined;
        }
        current = current[part];
    }
    return current;
}

function setDeepProperty(obj: AnyObject, path: string, value: any): void {
    const parts = path.split(".");
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (typeof current[part] !== "object" || current[part] === null) {
            current[part] = {};
        }
        current = current[part];
    }
    if (parts[parts.length - 1] === 'version') {
        current[parts[parts.length - 1]] = -1;
    }
    else {
        current[parts[parts.length - 1]] = value;
    }
}

function extractProperties(src: AnyObject, properties: string[]): AnyObject {
    const result: AnyObject = {};
    for (let prop of properties) {
        const value = getDeepProperty(src, prop);
        if (value !== undefined) {
            setDeepProperty(result, prop, value);
        }
    }
    return result;
}
export function trimDownApprovedSeries(approvedSeries: ApprovedSeries[]) {
    let approvedSeriesProperties = [
        ApprovedSeriesProperties.Id,
        ApprovedSeriesProperties.Version,
        ApprovedSeriesProperties.Owner,
        ApprovedSeriesProperties.SeriesId,
        ApprovedSeriesProperties.Created,
        ApprovedSeriesProperties.Updated,
    ];
    let processedSeriesProperties = [
        ProcessedMediaProperties.Id,
        ProcessedMediaProperties.Name,
        ProcessedMediaProperties.Description,
        ProcessedMediaProperties.Author,
        ProcessedMediaProperties.Created,
        ProcessedMediaProperties.Owner,
        ProcessedMediaProperties.Updated,
        ProcessedMediaProperties.Version,
        ProcessedMediaProperties.MediaId
    ]
    return approvedSeries.map((approvedSeries: ApprovedSeries) => {
        const selectedProps = extractProperties(approvedSeries, approvedSeriesProperties);
        selectedProps.processedSeries = extractProperties(approvedSeries.processedSeries, processedSeriesProperties);
        if (approvedSeries?.processedSeries) {
            selectedProps.processedSeries = trimDownProcessedMedia(approvedSeries?.processedSeries)
        }
        selectedProps.seasons = approvedSeries.seasons.map((season) => {
            let seas = extractProperties(season, [
                SeasonProperties.Created,
                SeasonProperties.Deleted,
                SeasonProperties.Description,
                SeasonProperties.Id,
                SeasonProperties.MediaStatus,
                SeasonProperties.Name,
                SeasonProperties.Owner,
                SeasonProperties.SeriesId,
                SeasonProperties.Updated,
                SeasonProperties.Version,
            ])
            seas.media = season.media.map((media) => trimDownMedia(media as any))
            return seas;
        })
        return selectedProps;
    })
}
export function trimdownCuratedSeries(curatedSeries: CuratedSeriesList) {
    const mediaProperties = [
        CuratedSeriesListProperties.Active,
        CuratedSeriesListProperties.Created,
        CuratedSeriesListProperties.Deleted,
        CuratedSeriesListProperties.Description,
        CuratedSeriesListProperties.Id,
        CuratedSeriesListProperties.Name,
        CuratedSeriesListProperties.Owner,
        CuratedSeriesListProperties.ProcessedSeries,
        CuratedSeriesListProperties.ReleaseZones,
        CuratedSeriesListProperties.Series,
        CuratedSeriesListProperties.Updated,
        CuratedSeriesListProperties.Version
    ]
    const selectedProps = extractProperties(curatedSeries, mediaProperties);
    selectedProps.processedSeries = trimDownProcessedMedias(selectedProps.processedSeries);
    return selectedProps;
}
export function trimdownCuratedSeriesList(curatedSeriesList: CuratedSeriesList[]) {
    return curatedSeriesList.map(trimdownCuratedSeries);
}
export function trimDownMedias(media: Media[]) {
    return media.map(trimDownMedia);
}
export function trimDownMedia(media: Media) {
    const mediaProperties = ["rating",
        "isPrivate",
        "mediaReactions.id",
        "mediaReactions.likes",
        "mediaReactions.love",
        "mediaReactions.dislikes",
        "mediaPreviewDuration",
        "transparentTitleUrl",
        "fire_id",
        "id",
        "owner",
        "thumbnail",
        "mediaPreviewUrl",
        "mediaPreviewLocation",
        "thumbnailLocation",
        "thumbnailDefaultUrl",
        "created._seconds",
        "created._nanoseconds",
        "mediaStatus",
        "mediaPreviewLocation",
        "mediaInformation.owner",
        "mediaInformation.id",
        "mediaInformation.version",
        "mediaInformation.name",
        "mediaInformation.mediaPreviewUrl",
        "mediaInformation.thumbnailLocation"
    ]
    return extractProperties(media, mediaProperties);
}
export function trimDownCuratedMediaList(curatedMediaList: CuratedMediaList[]) {
    const curatedMediaProperties = [
        CuratedMediaListProperties.Active,
        CuratedMediaListProperties.Name,
        CuratedMediaListProperties.Created,
        CuratedMediaListProperties.Deleted,
        CuratedMediaListProperties.Description,
        CuratedMediaListProperties.Id,
        CuratedMediaListProperties.ProcessedMedia,
        CuratedMediaListProperties.Released,
        CuratedMediaListProperties.Updated,
        CuratedMediaListProperties.Version
    ];
    return curatedMediaList.map((curatedMedia) => {
        const selectedProps = extractProperties(curatedMedia, curatedMediaProperties);
        selectedProps.media = Array.isArray(curatedMedia.media) ? trimDownMedias(curatedMedia.media) : [];
        selectedProps.processedMedia = trimDownProcessedMedias(curatedMedia.processedMedia);
        return selectedProps;
    })
}
export function trimDownApprovedMedia(approvedMedias: ApprovedMedia[]) {
    const approvedMediaProperties = [
        "owner",
        "id",
        "mediaId",
        "fire_id",
        "updated._seconds",
        "updated._nanoseconds",
        "version",
        "created._seconds",
        "created._nanoseconds",
        "media.rating",
        "media.isPrivate",
        "media.mediaReactions.id",
        "media.mediaReactions.likes",
        "media.mediaReactions.love",
        "media.mediaReactions.dislikes",
        "media.mediaPreviewDuration",
        "media.transparentTitleUrl",
        "media.fire_id",
        "media.id",
        "media.owner",
        "media.thumbnail",
        "media.created._seconds",
        "media.created._nanoseconds",
        "media.mediaStatus",
        "media.mediaPreviewLocation",
        "media.mediaInformation.owner",
        "media.mediaInformation.id",
        "media.mediaInformation.version",
        "media.mediaInformation.name",
        "media.mediaInformation.mediaPreviewUrl",
        "media.mediaInformation.thumbnailLocation",
    ];

    return approvedMedias.map((approvedMedia) => {
        const selectedProps = extractProperties(approvedMedia, approvedMediaProperties);
        if (selectedProps?.processedMedia && approvedMedia?.processedMedia?.states) {
            selectedProps.processedMedia = trimDownProcessedMedia(approvedMedia?.processedMedia)
        }
        return selectedProps;
    });
}

export function trimDownProcessedMedias(processedMedias: ProcessedMedia[]) {
    return processedMedias.map(pm => trimDownProcessedMedia(pm));
}

export function trimDownProcessedMedia(processedMedia: ProcessedMedia) {
    const procesedMediaProps = ["owner",
        "name",
        "id",
        "media",
        "mediaId",
        "media.version",
        "media.id",
        "media.name",
        "media.states"
    ]
    const processedMediaStatesProperties = [
        "owner",
        "resourcePropertyType",
        "mediaType",
        "resolution",
        "version",
        "sourceUrl",
        "codec",
        "deleted",
        "name",
        "mediaId",
        "ignore",
        "mediaRel",
        "sourceLocation",
        "location",
        "id",
        "imageType",
        "updated",
        "created"
    ];
    const selectedProps = extractProperties(processedMedia, procesedMediaProps);
    if (selectedProps && processedMedia?.states) {
        let videoWidth = 1080;
        let mediaRelStates = getProcessedMediaState(processedMedia?.states, ResourcePropertyType.MediaRel, MediaType.VIDEO, videoWidth);
        let mediaPreviewStates = getProcessedMediaState(processedMedia?.states, ResourcePropertyType.MediaPreviewUrl, MediaType.VIDEO, 720);
        let thumbNails = [
            ...getProcessedMediaState(processedMedia?.states, ResourcePropertyType.Thumbnail, MediaType.IMAGE, 300),
            ...getProcessedMediaState(processedMedia?.states, ResourcePropertyType.TitleMediaImageUrl, MediaType.IMAGE, 300)
        ];
        let titleMediaImageUrls = getProcessedMediaState(processedMedia?.states, ResourcePropertyType.TitleMediaImageUrl, MediaType.IMAGE, 300);
        let titleMediaUrls = getProcessedMediaState(processedMedia?.states, ResourcePropertyType.TitleMediaUrl, MediaType.VIDEO, 300);
        let titleTransparentUrls = getProcessedMediaState(processedMedia?.states, ResourcePropertyType.TitleTransparentUrl, MediaType.IMAGE, 300);
        let transparentTitleUrls = getProcessedMediaState(processedMedia?.states, ResourcePropertyType.TransparentTitleUrl, MediaType.IMAGE, 300);

        let gifsUrls = getProcessedMediaState(
            processedMedia?.states,
            ResourcePropertyType.GifPreview,
            MediaType.IMAGE,
            300
        );
        selectedProps.states = [
            mediaRelStates[0],
            mediaPreviewStates[0],
            thumbNails[0],
            titleMediaImageUrls[0],
            titleMediaUrls[0],
            titleTransparentUrls[0],
            transparentTitleUrls[0],
            gifsUrls[0]
        ].filter((x) => x);
        selectedProps.states = selectedProps.states.map((state: ProcessedMediaStates) => {
            return extractProperties(state, processedMediaStatesProperties);
        });
    }
    return selectedProps;
}
export function getProcessedMediaState(states: ProcessedMediaStates[], resourcePropertyType: ResourcePropertyType, mediaType: MediaType, width: number) {
    return states
        .filter((x: ProcessedMediaStates) => x.resourcePropertyType === resourcePropertyType && x.mediaType === mediaType)
        .sort((a: ProcessedMediaStates, b: ProcessedMediaStates) => {
            return Math.abs(a.width - width) - Math.abs(b.width - width);
        });
}
